/**
 * Servicio de ubicación — Portal Cliente Menuee
 *
 * Responsabilidad: geolocalización del cliente y selección por proximidad (Haversine).
 *
 * Qué nunca debe hacer:
 * - Consultar catálogo ni productos.
 * - Calcular cobertura, radios de reparto ni bloquear pedidos.
 * - Usar APIs externas (Mapbox, Google, etc.).
 * - Persistir sucursal (eso es sucursalPersistencia.js).
 */

/**
 * @typedef {Object} UbicacionCliente
 * @property {number} lat
 * @property {number} lon
 */

/**
 * @returns {boolean}
 */
export function geolocationDisponible() {
  return typeof navigator !== "undefined" && !!navigator.geolocation;
}

/**
 * Solicita permiso y obtiene la ubicación actual del navegador.
 *
 * @param {PositionOptions} [options]
 * @returns {Promise<UbicacionCliente>}
 */
export function solicitarPermisoUbicacion(options = {}) {
  return obtenerUbicacionActual(options);
}

/**
 * @param {PositionOptions} [options]
 * @returns {Promise<UbicacionCliente>}
 */
export function obtenerUbicacionActual(options = {}) {
  return new Promise((resolve, reject) => {
    if (!geolocationDisponible()) {
      reject(Object.assign(new Error("Geolocalización no soportada"), { code: "GEOLOCATION_UNSUPPORTED" }));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
        });
      },
      (err) => reject(err),
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
        ...options,
      }
    );
  });
}

/**
 * Distancia en metros entre dos puntos (fórmula de Haversine).
 *
 * @param {number} lat1
 * @param {number} lon1
 * @param {number} lat2
 * @param {number} lon2
 * @returns {number}
 */
export function distanciaHaversineMetros(lat1, lon1, lat2, lon2) {
  const R = 6371e3;
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * @param {Object} sucursal
 * @returns {{ lat: number, lon: number }|null}
 */
export function extraerCoordenadasSucursal(sucursal) {
  const lat = Number(sucursal?.latitud);
  const lon = Number(sucursal?.longitud);

  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    return null;
  }

  return { lat, lon };
}

/**
 * Devuelve la sucursal activa más cercana con coordenadas válidas.
 * Ignora sucursales sin latitud/longitud.
 *
 * @param {UbicacionCliente} ubicacion
 * @param {Object[]} sucursales
 * @returns {Object|null}
 */
export function calcularSucursalMasCercana(ubicacion, sucursales) {
  if (!ubicacion || !Array.isArray(sucursales) || sucursales.length === 0) {
    return null;
  }

  let menorDistancia = Infinity;
  let sucursalCercana = null;

  for (const sucursal of sucursales) {
    const coords = extraerCoordenadasSucursal(sucursal);
    if (!coords) {
      continue;
    }

    const distancia = distanciaHaversineMetros(
      ubicacion.lat,
      ubicacion.lon,
      coords.lat,
      coords.lon
    );

    if (distancia < menorDistancia) {
      menorDistancia = distancia;
      sucursalCercana = sucursal;
    }
  }

  return sucursalCercana;
}

/**
 * Primera sucursal de la lista como fallback (orden del servicio).
 *
 * @param {Object[]} sucursales
 * @returns {Object|null}
 */
export function obtenerSucursalPorDefecto(sucursales) {
  if (!Array.isArray(sucursales) || sucursales.length === 0) {
    return null;
  }
  return sucursales[0];
}
